/**
 * Resolving the prose range a caret comment should wrap:
 * - Inside quotes → just the quotation (including the quote marks)
 * - Otherwise → the current sentence (including any quotation it contains)
 */

import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";

const STRAIGHT_QUOTE = '"';
const OPEN_CURLY = "“";
const CLOSE_CURLY = "”";

function stripMarkersWithMap(text: string): {
  clean: string;
  toOrig: number[];
} {
  let clean = "";
  const toOrig: number[] = [];
  let i = 0;
  while (i < text.length) {
    const slice = text.slice(i);
    const match = toMaybe(slice.match(/^<!--\s*\/?zw-c:[A-Za-z0-9_-]+\s*-->/));
    if (isJust(match)) {
      i += match.value[0].length;
      continue;
    }
    toOrig.push(i);
    clean += text.charAt(i);
    i += 1;
  }
  toOrig.push(i);
  return { clean, toOrig };
}

function findQuotePairs(text: string): Array<{ from: number; to: number }> {
  const pairs: Array<{ from: number; to: number }> = [];
  let i = 0;
  while (i < text.length) {
    const ch = text.charAt(i);
    if (ch === OPEN_CURLY || ch === STRAIGHT_QUOTE) {
      const open = i;
      const closer = ch === OPEN_CURLY ? CLOSE_CURLY : STRAIGHT_QUOTE;
      i += 1;
      while (
        i < text.length &&
        text.charAt(i) !== closer &&
        text.charAt(i) !== "\n"
      ) {
        i += 1;
      }
      if (i < text.length && text.charAt(i) === closer) {
        pairs.push({ from: open, to: i + 1 });
        i += 1;
        continue;
      }
      // Unclosed. Abandon this opener.
      continue;
    }
    i += 1;
  }
  return pairs;
}

function findEnclosingQuoteRange(
  text: string,
  pos: number,
): Maybe<{ from: number; to: number }> {
  for (const pair of findQuotePairs(text)) {
    // Caret on/inside the quotation (including the quote marks).
    if (pos >= pair.from && pos < pair.to) return just(pair);
  }
  return nothing;
}

function isIndexInsideQuote(text: string, index: number): boolean {
  for (const pair of findQuotePairs(text)) {
    if (index > pair.from && index < pair.to - 1) return true;
  }
  return false;
}

function findSentenceRange(
  text: string,
  pos: number,
): { from: number; to: number } {
  const n = text.length;
  let p = Math.max(0, Math.min(pos, n));

  // Prefer the following sentence when caret sits in the gap after a terminator.
  const atP = text.charAt(p);
  const beforeP = text.charAt(p - 1);
  if (
    p > 0 &&
    p < n &&
    /\s/.test(atP) &&
    /[.!?]/.test(beforeP) &&
    isIndexInsideQuote(text, p - 1) === false
  ) {
    while (p < n) {
      const ws = text.charAt(p);
      if (/\s/.test(ws) === false) break;
      p += 1;
    }
  }

  let from = p;
  while (from > 0) {
    const prev = text.charAt(from - 1);
    if (prev === "\n") break;
    const atFrom = text.charAt(from);
    if (
      /[.!?]/.test(prev) &&
      isIndexInsideQuote(text, from - 1) === false &&
      (from >= n || /\s/.test(atFrom) || from === p)
    ) {
      break;
    }
    from -= 1;
  }
  while (from < n) {
    const ws = text.charAt(from);
    if (/\s/.test(ws) === false) break;
    from += 1;
  }

  let to = Math.max(p, from);
  while (to < n) {
    const ch = text.charAt(to);
    if (ch === "\n") break;
    if (/[.!?]/.test(ch) && isIndexInsideQuote(text, to) === false) {
      to += 1;
      // Include a closing quote glued to the terminator: She said "Hi!".
      while (to < n) {
        const q = text.charAt(to);
        if (q !== STRAIGHT_QUOTE && q !== CLOSE_CURLY) break;
        to += 1;
      }
      break;
    }
    to += 1;
  }

  // Trim trailing whitespace (keep sentence punctuation).
  while (to > from) {
    const trail = text.charAt(to - 1);
    if (/\s/.test(trail) === false) break;
    to -= 1;
  }
  return { from, to };
}

export function resolveCommentWrapRange(
  doc: string,
  pos: number,
): Maybe<{ from: number; to: number }> {
  const proseEnd = doc.length;
  const caret = Math.max(0, Math.min(pos, proseEnd));

  // Work within the current paragraph (blank-line separated).
  const before = doc.slice(0, caret);
  const after = doc.slice(caret, proseEnd);
  const paraStartBreak = before.lastIndexOf("\n\n");
  const paraEndBreak = after.indexOf("\n\n");
  let paraFrom = paraStartBreak === -1 ? 0 : paraStartBreak + 2;
  let paraTo = paraEndBreak === -1 ? proseEnd : caret + paraEndBreak;
  // Trim to avoid wrapping leading/trailing blank lines from the split.
  while (paraFrom < paraTo && doc[paraFrom] === "\n") paraFrom += 1;
  while (paraTo > paraFrom && doc[paraTo - 1] === "\n") paraTo -= 1;
  if (paraTo <= paraFrom) return nothing;

  const para = doc.slice(paraFrom, paraTo);
  const localPos = Math.max(0, Math.min(caret - paraFrom, para.length));
  const { clean, toOrig } = stripMarkersWithMap(para);
  if (clean.trim().length === 0) return nothing;

  // Map caret into cleaned prose (skipping hidden markers).
  let cleanPos = 0;
  while (cleanPos < clean.length) {
    const orig = toMaybe(toOrig[cleanPos]);
    if (isNothing(orig) || orig.value >= localPos) break;
    cleanPos += 1;
  }
  // If caret sits inside a marker, snap to the following clean char.
  if (cleanPos > 0) {
    const prevOrig = toMaybe(toOrig[cleanPos - 1]);
    if (isJust(prevOrig) && prevOrig.value >= localPos) {
      cleanPos -= 1;
    }
  }

  const quote = findEnclosingQuoteRange(clean, cleanPos);
  const range = isJust(quote)
    ? quote.value
    : findSentenceRange(clean, cleanPos);
  if (range.to <= range.from) return nothing;
  if (clean.slice(range.from, range.to).trim().length === 0) return nothing;

  const fromOrig = toMaybe(toOrig[range.from]);
  const toMapped = toMaybe(toOrig[range.to]);
  if (isNothing(fromOrig) || isNothing(toMapped)) return nothing;

  return just({
    from: paraFrom + fromOrig.value,
    to: paraFrom + toMapped.value,
  });
}
